<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import { useInsightActionStore } from '../stores/insightActionStore'

import { useI18n } from '@/shared/i18n'
import { Alert } from '@/shared/ui/alert'

const SLOW_AFTER_MS = 30_000

const { t } = useI18n()
const store = useInsightActionStore()

const counts = computed(
  () => store.run?.counts ?? { succeeded: 0, failed: 0, awaitingApproval: 0, skipped: 0 },
)
const total = computed(() => store.run?.totalItems ?? 0)
const done = computed(() => store.runProgress?.done ?? total.value)
const percent = computed(() => (total.value ? Math.round((done.value / total.value) * 100) : 0))

const counters = computed(() => [
  { key: 'applied', value: counts.value.succeeded, label: t('insightActions.progress.applied') },
  {
    key: 'waiting',
    value: counts.value.awaitingApproval,
    label: t('insightActions.progress.waiting'),
  },
  { key: 'skipped', value: counts.value.skipped, label: t('insightActions.progress.skipped') },
  { key: 'failed', value: counts.value.failed, label: t('insightActions.progress.failed') },
])

const slow = ref(false)
let timer: ReturnType<typeof setTimeout> | undefined
onMounted(() => {
  timer = setTimeout(() => {
    slow.value = true
  }, SLOW_AFTER_MS)
})
onBeforeUnmount(() => clearTimeout(timer))
</script>

<template>
  <div class="insight-action-progress grid gap-md min-w-0">
    <div class="h-2 overflow-hidden rounded-full bg-surface-muted">
      <div
        class="h-full rounded-full bg-primary transition-[width] duration-500"
        :style="{ width: `${percent}%` }"
        role="progressbar"
        :aria-valuenow="done"
        :aria-valuemin="0"
        :aria-valuemax="total"
        :aria-label="t('insightActions.progress.label', { done, total })"
      ></div>
    </div>

    <div class="flex flex-wrap items-baseline gap-x-md gap-y-2xs text-xs" aria-live="polite">
      <span v-for="counter in counters" :key="counter.key" :data-counter="counter.key">
        <b class="mr-2xs text-sm">{{ counter.value }}</b
        >{{ counter.label }}
      </span>
      <span class="ml-auto text-muted">
        {{ t('insightActions.progress.stillRunning', { count: Math.max(total - done, 0) }) }}
      </span>
    </div>

    <Alert
      v-if="slow"
      tone="success"
      :title="t('insightActions.progress.slow')"
      data-testid="progress-slow"
    />

    <p class="m-0 text-xs text-muted">{{ t('insightActions.progress.explainer') }}</p>
  </div>
</template>
